/**
 * File: article_page.tsx
 * Description: Public page that renders a single FAQ article's full content alongside a sidebar
 * listing other articles in the same category. Category and article are resolved from URL
 * search parameters (idcategoria and idarticulo).
 */

import {
  Box,
  Typography,
  useTheme,
  List,
  ListItem,
  ListItemText,
  Link,
} from "@mui/material";
import { useSearchParams } from 'react-router-dom';
import { Link as RouterLink } from 'react-router-dom';
import MDEditor from '@uiw/react-md-editor';
import Header from "@/components/global/header";
import { useCategoriesContext } from "@/context/public/categories_context";
import { useThemeModeContext } from '@/context/theme/theme_mode_context';
import { ColorsPalletTheme } from "@/constants/colors_pallet_theme";
import BreadcrumbNavegation from "@/components/public/breadcrumb_navegation";

/**
 * Renders the article detail page for the public FAQ interface.
 *
 * Reads `idcategoria` and `idarticulo` from the URL search params to locate the
 * matching category and article in the shared categories context. Shows a sidebar
 * with links to other articles in the same category and renders the selected
 * article's markdown content in the main panel.
 *
 * @returns {JSX.Element}
 */
function ArticlePage() {

  const { mode } = useThemeModeContext();
  const {categories, isLoading} = useCategoriesContext()

  // Main App Theme
  const theme = useTheme();
  const colors = ColorsPalletTheme(theme.palette.mode);

  const [searchParams] = useSearchParams();
  const idCategory = searchParams.get('idcategoria');
  const idArticle= searchParams.get('idarticulo');

  const articleCategory = categories.find((category) => (category.id === Number(idCategory)))
  const mainArticle = articleCategory?.articles.find((article) => (article.id === Number(idArticle)))

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh'}}>
      <Header />
      <BreadcrumbNavegation items={[
        {label: String(articleCategory?.title), to:`/categoria?idcategoria=${String(articleCategory?.id)}`},
        ]}/>
      <Box
        component="main"
        sx={{
          display: 'flex',
          justifyContent: "center",
          p: {xs:0, sm:3},
        }}
      >
        {
          isLoading && mainArticle ?
          <></>
          :
          <Box sx={{ display: 'flex', flexDirection: {xs:'column-reverse', sm:'row'}, gap: {xs:0, sm:2}, maxWidth: "100%"}}>
            <Box
              data-testid="sidebar-articles-list"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                padding: 3,
                paddingTop: {xs:0, sm:3},
                width: {xs:"100vw", sm:"300px"},
              }}>
              <Typography
                variant="h6"
                sx={{
                  paddingLeft:0,
                  display: { xs: 'flex', sm:'inline' },
                  justifyContent: { xs: 'center', sm: 'normal'},
                }}
              >
                Artículos en esta Categoria
              </Typography>
              {
                articleCategory && articleCategory.articles.length > 1 ?
                (
                  <List
                    dense
                    sx={{
                      paddingTop: 0,
                      paddingBottom: 0,
                    }}
                    >
                      {/* Reverse the list so newer articles appear first */}
                      {articleCategory.articles.toReversed().map(article => (
                        article.id !== Number(idArticle) ?
                        (
                          <ListItem
                            key={article.id}
                            disableGutters
                          >
                            <ListItemText
                              sx={{}}
                              primary={
                                <Link
                                  component={RouterLink}
                                  to={`/articulo?idcategoria=${String(articleCategory.id)}&idarticulo=${String(article.id)}`}
                                  sx={{ color: colors.blueAccent[500] }}
                                  underline="hover"
                                >
                                  <Typography
                                    sx={{
                                      fontSize:15,
                                    }}
                                  >
                                    {article.title}
                                  </Typography>
                                </Link>
                              }
                            />
                          </ListItem>
                        ) :
                        (<></>)
                      ))}
                  </List>
                ) :
                (
                  <Typography variant="h6">No hay más Artículos en esta Categoría</Typography>
                )
              }
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: {xs:1, sm:2}, padding: {xs:2, sm:3}, alignItems: "start", width: {xs:"100vw", sm:"800px"} }}>
              <Typography variant="h4"><strong>{mainArticle?.title}</strong></Typography>
              <Typography variant="body2">
                Última Actualización: {new Date(String(mainArticle?.creation_date)).toLocaleDateString('es-ES')}
              </Typography>
              <Box data-color-mode={mode} >
                <Box
                  component={MDEditor.Markdown}
                  source={String(mainArticle?.content)}
                  style={{ background: colors.default[100] }}
                  sx={{fontSize: {xs:14 , sm:16}}}
                ></Box>
              </Box>
              <Box sx={{ paddingTop: {xs:2, sm:5}, borderBottom: `2px solid ${colors.gray[800]}`, width: "100%" }}/>
            </Box>
          </Box>
        }
      </Box>
    </Box>
  );
}

export default ArticlePage;
